<template>
  <div
    :class="[
      {'chronology--collapsed': effectivelyCollapsed},
      {'first-is-reply': firstHistoryIsReply},
    ]"
    class="chronology"
  >
    <div v-if="hasActions" class="chronology__tabs">
      <dd-button
        :class="{ 'chronology__tab--active': !showChanges }"
        size="small"
        type="text"
        class="chronology__tab"
        @click="showChanges = false"
      >Comments</dd-button>
      <dd-button
        :class="{ 'chronology__tab--active': showChanges }"
        size="small"
        type="text"
        class="chronology__tab"
        @click="showChanges = true"
      >All activity</dd-button>
    </div>
    <transition-group :name="effectivelyCollapsed ? 'fade' : 'list'" mode="in-out" tag="div">
      <div
        v-for="(item, index) in exposedItems"
        :id="'e'+item.data.id"
        :key="item.data.id"
        :class="[`chronology__item--${getClassSuffix(item)}`, { 'chronology__item--highlight': 'e'+item.data.id === selectedEvent }]"
        class="chronology__item"
        @mouseenter="handleMouseEnter(item.data.id, index)"
        @mouseleave="handleMouseLeave(item.data.id, index)"
      >
        <!-- ACTION -->
        <chronology-action
          v-if="item.type === types.ACTION"
          :action-info="item.data"
          :visibility-note="visibilityNote"
        />

        <!-- COMMENT -->
        <chronology-comment
          v-else-if="item.type === types.COMMENT || item.type === types.INTERNAL_COMMENT"
          :rolled-over-comment="mousedOverItemID===item.data.id"
          :comment-info="item.data as IChronologyComment"
          :submit-handler="commentSubmitHandler"
          :visibility-note="visibilityNote"
          :is-internal-comment="item.type === types.INTERNAL_COMMENT"
        />

        <!-- REPLY -->
        <chronology-reply
          v-else-if="isReply(item)"
          :rolled-over-reply="mousedOverItemID===item.data.id"
          :is-reply-from-outside-user="item.type === types.REPLY_FROM_OUTSIDE_USER"
          :reply-info="item.data as IChronologyReply"
          :submit-handler="incomingReplySubmitHandler"
          @original-message-requested="(replyInfo) => $emit('original-message-requested', replyInfo)"
        />

        <!-- COLLAPSE BUTTON -->
        <div v-if="effectivelyCollapsed && index === 0" class="chronology__item-collapse">
          <dd-button size="medium" color="blue" type="capsule" @click="showAllItems">
            Show older updates ({{ initialHiddenItemCount }})
          </dd-button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import {
  IChronologyItem,
  IChronologyItemTypes,
  IChronologyComment,
  IChronologyReply,
} from '@/types/data-objects/item-histories'

import ChronologyAction from './components/chronology-action.vue'
import ChronologyReply from './components/chronology-reply.vue'
import ChronologyComment from './components/chronology-comment.vue'

export default defineComponent({
  name: 'Chronology',

  components: {
    ChronologyAction,
    ChronologyComment,
    ChronologyReply,
  },

  props: {
    items: {
      type: Array as () => IChronologyItem[],
      default: (): IChronologyItem[] => [],
    },

    visibilityNote: {
      type: String,
      default: '',
      required: false,
    },

    minInitialItemCountForCollapse: {
      type: Number,
      default: 4,
    },

    commentSubmitHandler: {
      type: Function,
      default: (): any => {},
    },

    incomingReplySubmitHandler: {
      type: Function,
      default: (): any => {},
    },

    isForTask: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['original-message-requested'],

  data() {
    return {
      types: IChronologyItemTypes,
      isCollapsed: false,
      initialExposedItemIDs: [] as number[],
      initialMostRecentCreatedOnEpochTime: 0,
      mousedOverItemID: null as any,
      showChanges: false,
    }
  },

  computed: {
    exposedItems(): IChronologyItem[] {
      let items: IChronologyItem[]
      if (!this.isCollapsed) {
        items = this.items
      } else {
        // First, grab items that are initially exposed
        const result = this.items.filter(i => {
          return this.initialExposedItemIDs.includes(i.data.id)
        })

        // Then, add any newer items since the initial data fetch.
        // (Comments, status updates, etc.)
        const newerItems = this.items.filter(i => {
          return (
            i.data.createdOnEpochTime > this.initialMostRecentCreatedOnEpochTime
          )
        })

        items = newerItems.length > 0 ? result.concat(newerItems) : result
      }

      if (!this.showChanges) {
        items = items.filter(i => i.type !== IChronologyItemTypes.ACTION)
      }
      return items
    },

    hasActions(): boolean {
      return this.items.some(i => i.type === IChronologyItemTypes.ACTION)
    },

    initialHiddenItemCount(): number {
      if (!this.isCollapsed) {
        return 0
      }
      const itemsIfFullyExpanded = this.showChanges
        ? this.items
        : this.items.filter(i => i.type !== IChronologyItemTypes.ACTION)
      const hidden = itemsIfFullyExpanded.length - this.exposedItems.length
      // Adding 1 for the first item which is partially exposed in the UI but considered "hidden"
      return hidden > 0 ? hidden + 1 : 0
    },

    effectivelyCollapsed(): boolean {
      return this.isCollapsed && this.initialHiddenItemCount > 0
    },

    selectedEvent(): string {
      return this.$route.hash.replace('#', '')
    },

    firstHistoryIsReply(): boolean {
      return (
        this.items &&
        this.items.length > 0 &&
        (this.items[0].type === IChronologyItemTypes.REPLY_FROM_OUTSIDE_USER ||
          this.items[0].type === IChronologyItemTypes.REPLY_TO_CUSTOMER)
      )
    },
  },

  watch: {
    items: {
      deep: 1,
      immediate: true,
      handler(newVal: IChronologyItem[], oldVal: IChronologyItem[]) {
        const initialData = oldVal && oldVal.length === 0
        if (initialData) {
          const hasRouteHash: boolean = this.$route.hash ? true : false
          this.setInitialItemsDisplay(this.items, hasRouteHash)
          if (hasRouteHash) {
            this.scrollToItem(this.$route.hash)
          }
        }
      },
    },
  },

  methods: {
    handleMouseEnter(id: number, index: number) {
      /* Don't let first history item to react in collapsed mode */
      if (this.effectivelyCollapsed && index === 0) {
        return
      }
      this.mousedOverItemID = id
    },
    handleMouseLeave(id: number, index: number) {
      /* Don't let first history item to react in collapsed mode */
      if (this.effectivelyCollapsed && index === 0) {
        return
      }
      this.mousedOverItemID = null
    },
    getClassSuffix(item: IChronologyItem) {
      if (this.isReply(item)) {
        return 'reply'
      } else if (this.isForTask && item.type === IChronologyItemTypes.COMMENT) {
        return 'task-comment'
      }
      return item.type
    },
    isReply(item: IChronologyItem) {
      return (
        item.type === IChronologyItemTypes.REPLY_FROM_OUTSIDE_USER ||
        item.type === IChronologyItemTypes.REPLY_TO_CUSTOMER
      )
    },
    setInitialItemsDisplay(
      initialItems: IChronologyItem[],
      hasRouteHash: boolean,
    ) {
      this.initialExposedItemIDs = initialItems.map(i => i.data.id)
      this.isCollapsed = false
      this.initialMostRecentCreatedOnEpochTime = Math.max.apply(
        Math,
        initialItems.map(i => {
          return i.data.createdOnEpochTime
        }),
      )

      // If there aren't many items or we're anchoring to a comment just show all of them!
      if (
        initialItems.length < this.minInitialItemCountForCollapse ||
        hasRouteHash
      ) {
        return
      }

      const unviewedItemIDs = initialItems
        .filter(i => {
          return !i.data.hasViewed
        })
        .map(i => i.data.id)

      // Also anchor on the first/last non-action items so the Comments tab
      // (which filters actions out) still has something to display when the
      // overall first/last items happen to be actions.
      const nonActionItems = initialItems.filter(
        i => i.type !== IChronologyItemTypes.ACTION,
      )
      const firstNonActionID =
        nonActionItems.length > 0 ? nonActionItems[0].data.id : null
      const lastNonActionID =
        nonActionItems.length > 0
          ? nonActionItems[nonActionItems.length - 1].data.id
          : null

      // Always show first and last item and any unviewed
      // We can safely assume unviewed items will appear together and at the bottom of the list
      this.initialExposedItemIDs = this.initialExposedItemIDs.filter(
        (itemID, idx) => {
          const isFirst = idx === 0
          const isLast = idx === initialItems.length - 1
          const isUnviewed = unviewedItemIDs.includes(itemID)
          const isFirstNonAction = itemID === firstNonActionID
          const isLastNonAction = itemID === lastNonActionID

          return (
            isFirst ||
            isLast ||
            isUnviewed ||
            isFirstNonAction ||
            isLastNonAction
          )
        },
      )

      // Set collapsed state if any items are hidden
      if (initialItems.length > this.initialExposedItemIDs.length) {
        this.isCollapsed = true
      }
    },

    async scrollToItem(id: string) {
      await this.$nextTick()
      const container = this.$el.querySelector(id)
      if (container) {
        container.scrollIntoView()
        window.scrollBy(0, -120)
      }
    },

    showAllItems() {
      this.isCollapsed = false
    },
  },
})
</script>

<style lang="scss">
.chronology {
  &__tabs {
    display: flex;
    padding: 20px $detail-gutter-pad-sm 12px;
    border-bottom: $border--solid-light;

    @include media-breakpoint-up(md) {
      padding-right: $detail-gutter-pad-md;
      padding-left: $detail-gutter-pad-md;
    }

    @include media-breakpoint-up(lg) {
      padding-right: $detail-gutter-pad-lg;
      padding-left: $detail-gutter-pad-lg;
    }
  }

  &__tab {
    position: relative;
    padding: 0;
    margin-right: 24px;
    @include lettering--p-sm;
    font-weight: $bold;

    &--active.dd-button--text {
      color: var(--color-text-secondary);

      &::after {
        position: absolute;
        right: 0;
        bottom: -13px;
        left: 0;
        height: 2px;
        content: '';
        background: $color--blue;
        border-radius: 1px;
      }
    }
  }

  &__item {
    padding: 28px $detail-gutter-pad-sm;

    &:first-child {
      @include media-breakpoint-up(md) {
        margin-top: 0px;
      }
    }

    &--action,
    &--comment,
    &--internal-comment {
      background-color: var(--color-background-secondary);

      @include media-breakpoint-up(md) {
        padding-right: $detail-gutter-pad-md;
        padding-left: $detail-gutter-pad-md - (0.5 * $detail-gutter-pad-md);
      }

      @include media-breakpoint-up(lg) {
        padding-right: $detail-gutter-pad-lg;
        padding-left: $detail-gutter-pad-lg - (0.5 * $detail-gutter-pad-lg);
      }
    }

    &--reply,
    &--task-comment {
      background-color: var(--color-background-header);

      @include media-breakpoint-up(md) {
        padding-right: $detail-gutter-pad-md;
        padding-left: $detail-gutter-pad-md - (0.5 * $detail-gutter-pad-md);
      }

      @include media-breakpoint-up(lg) {
        padding-right: $detail-gutter-pad-lg;
        padding-left: $detail-gutter-pad-lg - (0.5 * $detail-gutter-pad-lg);
      }
    }

    /* Creates bottom border from reply to non-reply */
    &--reply + &--reply,
    &--reply + &--action,
    &--reply + &--comment,
    &--comment + &--reply,
    &--comment + &--action,
    &--comment + &--comment,
    &--action + &--reply,
    &--action + &--comment {
      border-top: 1px dotted $color--gray-30;
    }

    /* Tighten actions */
    &--action + &--action {
      padding-top: 0;
    }
  }

  &--collapsed {
    /* Background color/gradient for reply item if first... */
    &.first-is-reply {
      .chronology__item {
        &-collapse {
          background-color: var(--color-background-header);
        }

        &:first-child::after {
          background-image: linear-gradient(
            to bottom,
            rgba(250, 249, 246, 0),
            $color--white
          );
        }
      }
    }

    .chronology__item {
      position: relative;
      margin: 0;

      &-collapse {
        display: flex;
        justify-content: center;
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 3;
        padding-bottom: 8px;
        background-color: var(--color-background-primary);
        border-bottom: 1px solid $silver-two;
      }

      &:first-child {
        height: 124px;
        overflow: hidden;

        &::before {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 1;
          content: '';
        }

        &::after {
          position: absolute;
          right: 0;
          bottom: 40px;
          left: 0;
          z-index: 2;
          height: 50px;
          content: '';
          background-image: linear-gradient(
            to bottom,
            rgba(250, 249, 246, 0),
            var(--color-background-primary)
          );
        }
      }

      // First item after collapse...
      &:nth-child(2) {
        padding-top: 32px;
      }
    }
  }

  /* List transition */
  .list-enter-active,
  .list-leave-active {
    transition: all 250ms ease-in-out;
  }

  .list-enter-from,
  .list-leave-to {
    opacity: 0;
    transform: translateY(-30px);
  }
}
</style>
